import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import Layout from './components/layout/Layout';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';
import VendorLayout from './components/layout/VendorLayout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import ServerErrorPage from './pages/ServerErrorPage';
import WishlistPage from './pages/account/WishlistPage';
import CartPage from './pages/CartPage';
import BrandsPage from './pages/BrandsPage'; // Client-side brands page
import BrandDetailPage from './pages/BrandDetailPage'; // Brand detail page
import AllCategoriesPage from './pages/AllCategoriesPage'; // Import the AllCategoriesPage component
import NewClinicSetupPage from './pages/NewClinicSetupPage';
import ClinicPackageDetailPage from './pages/ClinicPackageDetailPage';
import BestSellersPage from './pages/BestSellersPage';
import EventsPage from './pages/EventsPage'; // Import the BestSellersPage component
import PublicBlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
// Import account pages
import DashboardPage from './pages/account/DashboardPage';
import OrdersPage from './pages/account/OrdersPage';
import OrderDetailPage from './pages/account/OrderDetailPage';
import ProfilePage from './pages/account/ProfilePage';
import SettingsPage from './pages/account/SettingsPage';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';

// Import admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import BlogPage from './pages/admin/BlogPage';
import MediaPage from './pages/admin/MediaPage';
import UsersPage from './pages/admin/UsersPage';
import CustomersPage from './pages/admin/CustomersPage';
import UserActivityPage from './pages/admin/UserActivityPage';
import OnlineUsersPage from './pages/admin/OnlineUsersPage';
import CustomerOrdersPage from './pages/admin/CustomerOrdersPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import InvoicePrintPageWrapper from './pages/admin/InvoicePrintPage';
import CartsPage from './pages/admin/CartsPage';
import ProductsPage from './pages/admin/ProductsPage';
import CreateProductPage from './pages/admin/CreateProductPage';
import EditProductPage from './pages/admin/EditProductPage';
import VendorCreateProductPage from './pages/vendor/CreateProductPage';
import VendorEditVendorProductPage from './pages/vendor/EditProductPage';
import ViewProductPage from './pages/admin/ViewProductPage'; // Import the ViewProductPage component
import CategoriesPage from './pages/admin/CategoriesPage';
import AdminBrandsPage from './pages/admin/BrandsPage'; // Renamed to AdminBrandsPage
import VendorsPage from './pages/admin/VendorsPage';
import CreateVendorPage from './pages/admin/CreateVendorPage';
import VendorDetailsPage from './pages/admin/VendorDetailsPage';
import VendorEditPage from './pages/admin/VendorEditPage';
import BulkOrdersPage from './pages/admin/BulkOrdersPage';
import BulkOrderDetailPage from './pages/admin/BulkOrderDetailPage';
import { LoadingProvider } from './contexts/LoadingContext';
import VendorPageErrorBoundary from './components/error/VendorPageErrorBoundary';
import VendorOrdersPage from './pages/vendor/VendorOrdersPage';
import VendorOrderDetailPage from './pages/vendor/VendorOrderDetailPage';
import VendorProductsPage from './pages/vendor/ProductsPage';
import InventoryPage from './pages/vendor/InventoryPage';
import AdminSettings from './pages/admin/SettingsPage';
import HomepageSettingsPage from './pages/admin/settings/HomepageSettingsPage';
import NavigationSettingsPage from './pages/admin/settings/NavigationSettingsPage';
import SeoSettingsPage from './pages/admin/settings/SeoSettingsPage';
import SocialSettingsPage from './pages/admin/settings/SocialSettingsPage';
import ContactSettingsPage from './pages/admin/settings/ContactSettingsPage';
import EmailSettingsPage from './pages/admin/settings/EmailSettingsPage';
import BrandsPageSettingsPage from './pages/admin/settings/BrandsPageSettingsPage';
import ClinicSetupPage from './pages/admin/ClinicSetupPage';
import ClinicInquiriesPage from './pages/admin/ClinicInquiriesPage';
import AttributesSettingsPage from './pages/admin/AttributesSettingsPage';
import ReviewsPage from './pages/admin/ReviewsPage'; // Import ReviewsPage component
import AdminChatPage from './pages/admin/AdminChatPage'; // Import AdminChatPage component
import SalesPage from './pages/admin/SalesPage'; // Import SalesPage component
import NearExpiryPage from './pages/admin/NearExpiryPage'; // Import NearExpiryPage component
import PublicSalesPage from './pages/SalesPage'; // Import PublicSalesPage component
import PublicNearExpiryPage from './pages/NearExpiryPage'; // Import PublicNearExpiryPage component
import AdminEventsPage from './pages/admin/AdminEventsPage';
import SearchAnalyticsPage from './pages/admin/SearchAnalyticsPage';
// Import vendor pages
import VendorLoginPage from './pages/vendor/LoginPage';
import VendorDashboard from './pages/vendor/DashboardPage';
import VendorReportsPage from './pages/vendor/ReportsPage';
import VendorSettingsPage from './pages/vendor/SettingsPage';
import VendorForgotPasswordPage from './pages/vendor/ForgotPasswordPage';
import VendorResetPasswordPage from './pages/vendor/ResetPasswordPage';
import VendorRoute from './components/auth/VendorRoute';

// Import additional pages
import AllProductsPage from './pages/AllProductsPage';
import ProductDetailPage from './pages/ProductDetailPage'; // Import the ProductDetailPage component
import CategoryDetailPage from './pages/CategoryDetailPage'; // Import the CategoryDetailPage component
import SearchPage from './pages/SearchPage'; // Import the SearchPage component
import RadixUIShowcasePage from './pages/RadixUIShowcasePage'; // Import the Radix UI showcase page
import { CacheProvider } from './contexts/CacheContext'; // Add import for CacheProvider
import { SearchProvider } from './contexts/SearchContext'; // Add import for SearchProvider
import { WishlistProvider } from './contexts/WishlistContext'; // Import WishlistProvider
import { CartProvider } from './contexts/CartContext'; // Import CartProvider
import { ChatProvider } from './contexts/ChatContext'; // Import ChatProvider
import { NotificationProvider } from './contexts/NotificationContext'; // Import NotificationProvider
import { PushNotificationProvider } from './contexts/PushNotificationContext'; // Import PushNotificationProvider
import CartCleanupNotification from './components/cart/CartCleanupNotification'; // Import cart cleanup notification
import { ChatWidget } from './components/ui'; // Import ChatWidget
import { useMetaPixelPageTracking } from './hooks/useMetaPixel'; // Import Meta Pixel hook
import { TrackingProvider } from './contexts/TrackingContext'; // Import TrackingProvider
import { Toaster } from 'react-hot-toast'; // Import Toaster for global notifications

// Meta Pixel Page Tracker Component
function MetaPixelPageTracker() {
  useMetaPixelPageTracking();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <CacheProvider>
          <SocketProvider>
            <AuthProvider>
              <MetaPixelPageTracker />
              <ChatProvider>
                <NotificationProvider>
                  <PushNotificationProvider>
                    <WishlistProvider>
                      <TrackingProvider>
                        <CartProvider>
                          <LoadingProvider>
                            <SearchProvider>
                              <Routes>
                                {/* Main Website Routes */}
                              <Route element={<Layout />}>
                                {/* Public Routes */}
                                <Route path="/" element={<HomePage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/signup" element={<SignupPage />} />
                                <Route path="/verify-email" element={<EmailVerificationPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                                <Route path="/category/:slug" element={<CategoryDetailPage />} />
                                <Route path="/categories" element={<AllCategoriesPage />} />
                                <Route path="/product/:slug" element={<ProductDetailPage />} />
                                <Route path="/products" element={<AllProductsPage />} />
                                <Route path="/brands" element={<BrandsPage />} />
                                <Route path="/clinic-setup" element={<NewClinicSetupPage />} />
                                <Route path="/clinic-setup/package/:id" element={<ClinicPackageDetailPage />} />
                                <Route path="/brands/:slug" element={<BrandDetailPage />} />
                                <Route path="/best-sellers" element={<BestSellersPage />} />
                                <Route path="/events" element={<EventsPage />} />
                                <Route path="/blog" element={<PublicBlogPage />} />
                                <Route path="/blog/:slug" element={<BlogDetailPage />} />
                                <Route path="/search" element={<SearchPage />} />
                                <Route path="/sales" element={<PublicSalesPage />} />
                                <Route path="/near-expiry" element={<PublicNearExpiryPage />} />
                                <Route path="/ui-showcase" element={<RadixUIShowcasePage />} />
                                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                                <Route path="/about" element={<AboutPage />} />

                                {/* Cart is public so guests can review/manage the items they've
                                    added before logging in - login is only required at checkout
                                    (the /checkout route below stays behind PrivateRoute). */}
                                <Route path="/cart" element={<CartPage />} />

                                {/* Protected Routes */}
                                <Route
                                  path="/checkout"
                                  element={
                                    <PrivateRoute requireVerification={true}>
                                      <CheckoutPage />
                                    </PrivateRoute>
                                  }
                                />
                                <Route
                                  path="/order-success"
                                  element={
                                    <PrivateRoute requireVerification={true}>
                                      <OrderSuccessPage />
                                    </PrivateRoute>
                                  }
                                />

                                {/* Dashboard Routes */}
                                <Route
                                  path="/account"
                                  element={
                                    <PrivateRoute requireVerification={true}>
                                      <DashboardLayout />
                                    </PrivateRoute>
                                  }
                                >
                                  <Route index element={<DashboardPage />} />
                                  <Route path="orders" element={<OrdersPage />} />
                                  <Route path="orders/:id" element={<OrderDetailPage />} />
                                  <Route path="wishlist" element={<WishlistPage />} />
                                  <Route path="profile" element={<ProfilePage />} />
                                  <Route path="settings" element={<SettingsPage />} />
                                </Route>

                                {/* Admin events route moved below */}

                                {/* Error Routes */}
                                <Route path="/server-error" element={<ServerErrorPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                              </Route>

                              {/* Admin Routes - Separate from main layout */}
                              <Route
                                path="/admin"
                                element={
                                  <AdminRoute>
                                    <LoadingProvider>
                                      <SearchProvider>
                                        <AdminLayout />
                                      </SearchProvider>
                                    </LoadingProvider>
                                  </AdminRoute>
                                }
                              >
                                <Route index element={<AdminDashboard />} />
                                <Route path="events" element={<AdminEventsPage />} />
                                <Route path="products" element={<ProductsPage />} />
                                <Route path="products/create" element={<CreateProductPage />} />
                                <Route path="products/:id" element={<ViewProductPage />} />
                                <Route path="products/edit/:id" element={<EditProductPage />} />
                                <Route path="categories" element={<CategoriesPage />} />
                                <Route path="brands" element={<AdminBrandsPage />} />
                                <Route path="clinic-setup" element={<ClinicSetupPage />} />
                                <Route path="clinic-inquiries" element={<ClinicInquiriesPage />} />
                                <Route path="reviews" element={<ReviewsPage />} />
                                <Route path="searched-items" element={<SearchAnalyticsPage />} />
                                <Route
                                  path="vendors"
                                  element={
                                    <VendorPageErrorBoundary>
                                      <VendorsPage />
                                    </VendorPageErrorBoundary>
                                  }
                                />
                                <Route
                                  path="vendors/create"
                                  element={
                                    <VendorPageErrorBoundary>
                                      <CreateVendorPage />
                                    </VendorPageErrorBoundary>
                                  }
                                />
                                <Route
                                  path="vendors/:vendorId"
                                  element={
                                    <VendorPageErrorBoundary>
                                      <VendorDetailsPage />
                                    </VendorPageErrorBoundary>
                                  }
                                />
                                <Route
                                  path="vendors/edit/:vendorId"
                                  element={
                                    <VendorPageErrorBoundary>
                                      <VendorEditPage />
                                    </VendorPageErrorBoundary>
                                  }
                                />
                                <Route path="orders" element={<AdminOrdersPage />} />
                                <Route path="orders/:id" element={<AdminOrderDetailPage />} />
                                <Route path="bulk-orders" element={<BulkOrdersPage />} />
                                <Route path="bulk-orders/:id" element={<BulkOrderDetailPage />} />
                                <Route path="carts" element={<CartsPage />} />
                                <Route path="customers" element={<CustomersPage />} />
                                <Route path="customers/:customerId/orders" element={<CustomerOrdersPage />} />
                                <Route path="user-activity" element={<UserActivityPage />} />
                                <Route path="online-users" element={<OnlineUsersPage />} />
                                <Route path="blog" element={<BlogPage />} />
                                <Route path="media" element={<MediaPage />} />
                                <Route path="users" element={<UsersPage />} />
                                <Route path="notifications" element={<NotificationsPage />} />
                                <Route path="chat" element={<AdminChatPage />} />
                                <Route path="sales" element={<SalesPage />} />
                                <Route path="near-expiry" element={<NearExpiryPage />} />
                                <Route path="settings" element={<AdminSettings />}>
                                  <Route index element={<Navigate to="homepage" replace />} />
                                  <Route path="homepage" element={<HomepageSettingsPage />} />
                                  <Route path="navigation" element={<NavigationSettingsPage />} />
                                  <Route path="seo" element={<SeoSettingsPage />} />
                                  <Route path="social" element={<SocialSettingsPage />} />
                                  <Route path="contact" element={<ContactSettingsPage />} />
                                  <Route path="email" element={<EmailSettingsPage />} />
                                  <Route path="brands-page" element={<BrandsPageSettingsPage />} />
                                  <Route path="attributes" element={<AttributesSettingsPage />} />
                                </Route>
                              </Route>

                              {/* Standalone Invoice Print Route - No Admin Layout */}
                              <Route
                                path="/admin/invoice-print/:id"
                                element={
                                  <AdminRoute>
                                    <InvoicePrintPageWrapper />
                                  </AdminRoute>
                                }
                              />

                              {/* Vendor Routes */}
                              <Route path="/vendor/login" element={<VendorLoginPage />} />
                              <Route path="/vendor/forgot-password" element={<VendorForgotPasswordPage />} />
                              <Route path="/vendor/reset-password" element={<VendorResetPasswordPage />} />
                              <Route
                                path="/vendor"
                                element={
                                  <VendorRoute>
                                    <LoadingProvider>
                                      <VendorLayout />
                                    </LoadingProvider>
                                  </VendorRoute>
                                }
                              >
                                <Route index element={<Navigate to="/vendor/dashboard" replace />} />
                                <Route path="dashboard" element={<VendorDashboard />} />
                                <Route path="products" element={<VendorProductsPage />} />
                                <Route path="products/create" element={<VendorCreateProductPage />} />
                                <Route path="products/edit/:id" element={<VendorEditVendorProductPage />} />
                                <Route path="products/:id" element={<ViewProductPage />} />
                                <Route path="orders" element={<VendorOrdersPage />} />
                                <Route path="orders/:id" element={<VendorOrderDetailPage />} />
                                <Route path="inventory" element={<InventoryPage />} />
                                <Route path="reports" element={<VendorReportsPage />} />
                                <Route path="profile" element={<Navigate to="/vendor/settings" replace />} />
                                <Route path="settings" element={<VendorSettingsPage />} />
                              </Route>
                            </Routes>
                          </SearchProvider>
                        </LoadingProvider>
                        </CartProvider>
                      </TrackingProvider>
                    </WishlistProvider>
                  </PushNotificationProvider>
                </NotificationProvider>
              </ChatProvider>
            </AuthProvider>
          </SocketProvider>
        </CacheProvider>

        {/* Global Cart Cleanup Notification */}
        <CartCleanupNotification />
        
        {/* Global Toaster for Notifications */}
        <Toaster position="top-right" />

      </Router>
    </QueryClientProvider>
  );
}

export default App;